import axios, { AxiosRequestConfig } from 'axios';

export const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

const requestMaker = axios.create({
	baseURL: apiBaseUrl,
	withCredentials: true,
	withXSRFToken: true,
	xsrfCookieName: 'XSRF-TOKEN',
	xsrfHeaderName: 'X-XSRF-TOKEN',
});

export const AUTH_UNAUTHORIZED_EVENT = 'student-auth:unauthorized';

export function getApiStatusCode(error: unknown) {
	if (typeof error !== 'object' || error === null) return undefined;
	return (error as { response?: { status?: number } }).response?.status;
}

function isAuthBootstrapRequest(url?: string) {
	return (
		Boolean(url?.includes('/sign-in')) || Boolean(url?.includes('/validate'))
	);
}

function notifyUnauthorizedSession() {
	if (typeof window === 'undefined') return;
	window.dispatchEvent(new Event(AUTH_UNAUTHORIZED_EVENT));
}

function normalizeMessage(input: string) {
	return input
		.replace(/<[^>]+>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

function looksTechnicalOrUnsafe(raw: string, normalized: string) {
	return (
		/<!doctype|<html|<body|<head|<script|<div/i.test(raw) ||
		/ReferenceError|TypeError|SyntaxError|AxiosError|ThrottlerException|QueryFailedError|SQLSTATE|Sequelize|TypeORM|ECONN|EADDR|ENOTFOUND/i.test(raw) ||
		/\bat\s+.+\(.+\)/.test(raw) ||
		/^[{[]/.test(normalized) ||
		normalized.length > 220
	);
}

export function getApiErrorMessage(error: unknown, fallback = 'Request failed') {
	if (getApiStatusCode(error) === 429) {
		return 'Too many requests. Please wait a moment and try again.';
	}

	if (typeof error === 'object' && error !== null) {
		const candidate = error as {
			response?: {
				data?: { message?: string | string[]; error?: string } | string;
			};
			message?: string;
		};

		const responseData = candidate.response?.data;
		const rawMessage = Array.isArray(
			(responseData as { message?: string | string[] })?.message,
		)
			? (responseData as { message?: string[] }).message?.join(', ')
			: typeof (responseData as { message?: string })?.message === 'string'
				? (responseData as { message?: string }).message
				: typeof (responseData as { error?: string })?.error === 'string'
					? (responseData as { error?: string }).error
					: typeof responseData === 'string'
						? responseData
						: typeof candidate.message === 'string'
							? candidate.message
							: '';

		const normalized = normalizeMessage(rawMessage || '');
		if (!normalized) return fallback;
		if (looksTechnicalOrUnsafe(rawMessage || '', normalized)) return fallback;
		return normalized;
	}

	return fallback;
}

// Helper to merge optional headers / config
const withConfig = (config?: AxiosRequestConfig, useJson = true) => ({
	...config,
	headers: {
		...(useJson ? { 'Content-Type': 'application/json' } : {}),
		...(config?.headers || {}),
	},
});

const isFormData = (value: unknown): value is FormData =>
	typeof FormData !== 'undefined' && value instanceof FormData;

requestMaker.interceptors.response.use(
	(response) => response,
	(error) => {
		if (
			axios.isAxiosError(error) &&
			error.response?.status === 401 &&
			!isAuthBootstrapRequest(error.config?.url)
		) {
			notifyUnauthorizedSession();
		}

		return Promise.reject(error);
	},
);

export const api = {
	get: (url: string, params?: unknown, config?: AxiosRequestConfig) =>
		requestMaker.get(url, { params, ...withConfig(config) }),

	post: (url: string, data?: unknown, config?: AxiosRequestConfig) =>
		requestMaker.post(url, data, withConfig(config, !isFormData(data))),

	put: (url: string, data?: unknown, config?: AxiosRequestConfig) =>
		requestMaker.put(url, data, withConfig(config)),

	patch: (url: string, data?: unknown, config?: AxiosRequestConfig) =>
		requestMaker.patch(url, data, withConfig(config)),

	delete: (url: string, config?: AxiosRequestConfig) =>
		requestMaker.delete(url, withConfig(config)),
};
